import { useCallback } from "react";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import { ConfirmAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import {
  GALLERY_FOLDERS_REMOVE_FROM_FOLDER,
  GALLERY_FOLDERS_REMOVE_FROM_FOLDER_TEXT,
  GALLERY_FOLDERS_REMOVE_FROM_FOLDER_TITLE,
} from "../i18n/keysTranslations";

/**
 * Wraps a "remove from folder" action with a confirmation dialog.
 * If the first argument is a UI event, its propagation is stopped before awaiting.
 */
export const useConfirmRemoveFromFolder = (onRemoveFromFolder) => {
  const { t } = useTranslationApp();

  return useCallback(
    async (...args) => {
      args[0]?.stopPropagation?.();

      const confirmation = await ConfirmAlert({
        title: t(GALLERY_FOLDERS_REMOVE_FROM_FOLDER_TITLE),
        text: t(GALLERY_FOLDERS_REMOVE_FROM_FOLDER_TEXT),
        confirmButtonText: t(GALLERY_FOLDERS_REMOVE_FROM_FOLDER),
      });

      if (!confirmation?.isConfirmed) return undefined;

      return onRemoveFromFolder?.(...args);
    },
    [onRemoveFromFolder, t]
  );
};

/**
 * When a folder is active, the "delete" action of an item must become
 * "remove from folder" (with confirmation). Returns the label/icon/handler.
 */
export const useFolderAwareDeleteAction = ({
  activeFolderID = null,
  deleteLabel,
  deleteIcon,
  onDelete,
  onRemoveFromFolder,
}) => {
  const { t } = useTranslationApp();
  const isRemoveFromFolder = Boolean(activeFolderID);
  const confirmedRemoveFromFolder =
    useConfirmRemoveFromFolder(onRemoveFromFolder);

  return {
    isRemoveFromFolder,
    label: isRemoveFromFolder
      ? t(GALLERY_FOLDERS_REMOVE_FROM_FOLDER)
      : deleteLabel,
    Icon: isRemoveFromFolder ? FolderOffOutlinedIcon : deleteIcon,
    onAction: isRemoveFromFolder ? confirmedRemoveFromFolder : onDelete,
  };
};
